import { useMemo } from "react";
import { useCmsContext } from "@rotorstudios/fe-cms";
import { useLocation } from "@rotorstudios/fe-modules";
import { useQuery } from "@tanstack/react-query";

import { useFetchCMSData } from "./useFetchCMSData";
import {
  CMS_DATA_SOURCES,
  CMS_DATASOURCES_KILLSWITCH,
  QA_DISABLE_URL_PARAM,
} from "./static";

const VITE_BUILD_ENV = import.meta.env.VITE_BUILD_ENV;

const useKillSwitch = () => {
  const { search } = useLocation();
  const { fetchData } = useFetchCMSData();
  const { siloManifest } = useCmsContext();

  const urlParams = useMemo(() => new URLSearchParams(search), [search]);

  const path = siloManifest?.[CMS_DATA_SOURCES]?.get(CMS_DATASOURCES_KILLSWITCH);

  // *************************
  // CMS Kill Switch Data
  // *************************
  const getKillSwitchData = async (): Promise<any> => {
    if (!path) throw new Error("Kill Switch data source not found in silo manifest");
    const raw = await fetchData(path);
    return raw?.data;
  };

  const { data: features } = useQuery({
    queryKey: ["killswitch", path],
    queryFn: () => getKillSwitchData(),
    enabled: Boolean(path),
    refetchOnMount: false,
  });

  // *******************
  // Methods
  // *******************

  // Checks if feature is disabled in CMS by environment
  const isDisabledInCMSEnvironment = (feature: any): boolean =>
    feature?.disabledEnvironments?.includes(VITE_BUILD_ENV);

  // Checks if feature is disabled by QA feature flag
  const isDisabledInQAFlag = (id: string): boolean =>
    !!urlParams.get(QA_DISABLE_URL_PARAM)?.split(",")?.includes(id);

  // Checks if an option within a broader feature is disabled (e.g. individual option packs in the configurator)
  const isOptionDisabled = (
    feature: any,
    optionId: string,
    cmsDisabled: boolean,
    qaDisabled: boolean,
  ): boolean => {
    const option = feature?.[optionId];
    if (!option) return false;
    return cmsDisabled || qaDisabled;
  };

  const isFeatureDisabled = (id: string, optionId?: string): boolean => {
    const feature = features?.[id];

    if (!feature && !urlParams.has(QA_DISABLE_URL_PARAM)) {
      return false;
    }

    const cmsDisabled = isDisabledInCMSEnvironment(feature);
    const qaDisabled = isDisabledInQAFlag(id);

    if (optionId) {
      return isOptionDisabled(feature, optionId, cmsDisabled, qaDisabled);
    }

    return cmsDisabled || qaDisabled;
  };

  return { isFeatureDisabled };
};

export default useKillSwitch;
